import axios from "axios";
import { defaultSettings } from "contexts/appsettings/defaultSettings";
import { getClinicalLibraryHierarchyIds } from "contexts/hierarchy/getClinicalLibraryHierarchyIds";
import { LoaderFunction, LoaderFunctionArgs } from "react-router-dom";
import { REACT_APP_BACKENDURL } from "../../../config";
import { getSubscriptionStatusLoaderData } from "../comments/getSubscriptionStatusLoaderData";
import { eAccessRights } from "../enums";
import { ClinicalLibrariesReadDtoReturn, getLibraries } from "../library/getLibraries";
import { HierarchyReadDto } from "./HierarchyReadDto";

/**
 * Loader data returned by the hierarchy loader, including subloads.
 */
export interface HierarchyLoaderData {
  hierarchy: HierarchyReadDto;
  parentHierarchies: HierarchyReadDto[];
  accessRights: eAccessRights[];
  isHierarchySubscribed: boolean;
  clinicalLibraryPromise?: Promise<ClinicalLibrariesReadDtoReturn>;
}

/**
 * Data returned by the hierarchy loader API endpoint.
 */
export interface HierarchyLoaderReturn {
  hierarchy: HierarchyReadDto;
  parentHierarchies: HierarchyReadDto[];
  accessRights: eAccessRights[];
}

/**
 * Get the configured backend URL.
 *
 * @returns The backend base URL
 */
const getBackEndUrl = (): string =>
  REACT_APP_BACKENDURL.includes("_REACT_APP_BACKENDURL_")
    ? defaultSettings.backEndUrl
    : REACT_APP_BACKENDURL;

let activeClinicalLibraryController: AbortController | undefined;

/**
 * Construct the hierarchy loader API URL from route params.
 *
 * New routes pass the full ancestor path as query parameters
 * (`taPrefix`, `projectCode`, `studyTag`, `reportName`, `tocMajorVersion`),
 * so the backend can uniquely identify the hierarchy when tags are only
 * unique within their parent scope.
 *
 * Legacy params (`primaryHierarchyId`, `hierarchyTag`,
 * `studyTag` + `reportName`) are still handled for backward compatibility
 * with legacy template and shell routes.
 *
 * @param params - Loader function params containing hierarchy identifiers
 * @returns The constructed URL for the hierarchy loader API endpoint
 */
export const getHierarchyLoaderUrl = (params: LoaderFunctionArgs["params"]): string => {
  const queryParams = new URLSearchParams();

  if (params.primaryHierarchyId) {
    queryParams.set("primaryHierarchyId", params.primaryHierarchyId);
  }

  if (params.hierarchyTag) {
    queryParams.set("hierarchyTag", params.hierarchyTag);
  }

  if (params.taPrefix) {
    queryParams.set("taPrefix", params.taPrefix);
  }

  if (params.projectCode) {
    queryParams.set("projectCode", params.projectCode);
  }

  if (params.studyTag) {
    queryParams.set("studyTag", params.studyTag);
  }

  if (params.reportName) {
    queryParams.set("reportName", params.reportName);
  }

  if (params.tocMajorVersion !== undefined) {
    queryParams.set("tocMajorVersion", params.tocMajorVersion);
  }

  if (queryParams.size === 0) {
    queryParams.set("hierarchyTag", "Corporate");
  }

  return `${getBackEndUrl()}Hierarchy/Loader?${queryParams.toString()}`;
};

/**
 * Fetch hierarchy loader data and start clinical-library loading without
 * blocking the route loader.
 *
 * @param params - Loader function params containing hierarchy identifiers
 * @param signal - Optional navigation cancellation signal
 * @returns The hierarchy loader data
 */
export const getHierarchyLoaderData = async (
  params: LoaderFunctionArgs["params"],
  signal?: AbortSignal,
): Promise<HierarchyLoaderData> => {
  const hierarchyResponse = await axios.get<HierarchyLoaderReturn>(getHierarchyLoaderUrl(params), {
    signal,
  });

  if (!hierarchyResponse.data?.hierarchy) {
    throw new Error("Invalid response from HierarchyLoader, no data returned");
  }

  const { hierarchy, parentHierarchies, accessRights } = hierarchyResponse.data;

  const subscriptionStatusLoader = await getSubscriptionStatusLoaderData({
    params: {
      entityType: "Hierarchy",
      entityId: hierarchy.id,
    },
  });

  /*
   * The previous Clinical Library request belongs to the previous
   * hierarchy navigation and is no longer required.
   */
  activeClinicalLibraryController?.abort();
  activeClinicalLibraryController = undefined;

  const clinicalLibraryHierarchyIds = getClinicalLibraryHierarchyIds(hierarchy, hierarchy.id);

  let clinicalLibraryPromise: Promise<ClinicalLibrariesReadDtoReturn> | undefined;

  /* istanbul ignore else */
  if (clinicalLibraryHierarchyIds.length > 0) {
    const controller = new AbortController();
    activeClinicalLibraryController = controller;

    clinicalLibraryPromise = getLibraries(
      getBackEndUrl(),
      clinicalLibraryHierarchyIds,
      controller,
    ).finally(() => {
      /*
       * Only clear the active controller if this is still the latest
       * Clinical Library request.
       */
      if (activeClinicalLibraryController === controller) {
        activeClinicalLibraryController = undefined;
      }
    });
  }

  return {
    hierarchy,
    parentHierarchies,
    accessRights,
    isHierarchySubscribed: subscriptionStatusLoader.isSubscribed,
    clinicalLibraryPromise,
  };
};

/**
 * Loader function that returns hierarchy loader data used by routes.
 *
 * @param props - Loader args provided by react-router
 * @param props.params - Route params used to identify the hierarchy
 * @param props.request - Route navigation request
 * @returns The hierarchy loader data
 */
export const hierarchyLoader: LoaderFunction = async ({
  params,
  request,
}: LoaderFunctionArgs): Promise<HierarchyLoaderData> =>
  getHierarchyLoaderData(params, request.signal);
